import { CustomCard } from "#web/components/common/CustomCard";
import { makeShop, ShopQuery } from "#web/components/shop/queries/shopQuery";
import { routeConfig } from "#web/configs/route";
import { api } from "#web/services/api";
import { i18nLike } from "#web/services/i18nLike";
import { Badge, Banner, BlockStack, Box, InlineStack, Spinner, Text } from "@shopify/polaris";
import { DiscountIcon } from "@shopify/polaris-icons";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

const en = {
  activeLeakedCoupons: "active leaked coupons",
  error: {
    description: "Please try again. If the issue persists, please contact support.",
    title: "There was an error loading the leaks card",
  },
  primaryAction: "View",
  scanning: "Scanning for coupon leaks",
  title: "Leaked Codes",
  youHave: "You have",
};

const CurrentlyLeakedCodesCountQuery = () =>
  queryOptions({
    queryFn: () =>
      api.shopifyShop.findFirst({
        select: {
          currentlyLeakedCodeCount: true,
        },
      }),
    queryKey: ["currentlyLeakedCodesCount"],
  });

export const LeaksCard = () => {
  const t = i18nLike(en);
  const { data: shopData, isError: shopError, isFetching: shopFetching } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;
  const navigate = useNavigate();

  const {
    data: currentlyLeakedCodesCountData,
    isError: currentlyLeakedCodesCountError,
    isFetching: currentlyLeakedCodesCountFetching,
  } = useQuery(CurrentlyLeakedCodesCountQuery());
  // @ts-expect-error gelly is untyped
  const currentlyLeakedCodeCount = Number(currentlyLeakedCodesCountData?.currentlyLeakedCodeCount?.value ?? 0);

  const isError = shopError || currentlyLeakedCodesCountError;
  const isLoading = shopFetching || currentlyLeakedCodesCountFetching;
  const isScanning = shop?.initialScrapeStatus === "scraping";

  if (isError) {
    return (
      <Banner title={t("error.title")} tone="critical">
        {t("error.description")}
      </Banner>
    );
  }

  return (
    <CustomCard
      buttonText={t("primaryAction")}
      icon={DiscountIcon}
      loading={isLoading}
      onAction={() => {
        navigate(routeConfig.leaks["/"]);
      }}
      title={t("title")}
    >
      <Box paddingInlineStart="600">
        <BlockStack gap="200">
          {isScanning ? (
            <InlineStack gap="200">
              <Spinner accessibilityLabel="Loading" size="small" />
              <Text as="span">{t("scanning")}</Text>
            </InlineStack>
          ) : currentlyLeakedCodesCountFetching ? (
            <InlineStack align="center" gap="200">
              <Spinner accessibilityLabel="Loading" size="small" />
            </InlineStack>
          ) : (
            <>
              <InlineStack gap="100">
                <Text as="span">{t("youHave")}</Text>
                <Badge>
                  {/* @ts-expect-error Element is not assignable to type string */}
                  <Text as="span" fontWeight="bold" variant="bodyMd">
                    {currentlyLeakedCodeCount}
                  </Text>
                </Badge>
                <Text as="span">{t("activeLeakedCoupons")}</Text>
              </InlineStack>
            </>
          )}
        </BlockStack>
      </Box>
    </CustomCard>
  );
};
